import {LayerContext} from '@dropbox/dig-components/layer';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {atoms, Box, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AdminConsoleLine,
  AppsLine,
  CompassLine,
  GratitudeLine,
  HomeLine,
  NewsLine,
  PersonMultipleLine,
  TeamLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {logNavItemClicked} from 'analytics/navAnalytics';
import {pulseUserAtom} from 'atoms/auth';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {growthbookCacheAtom, isMobileAtom} from 'atoms/layout';
import cx from 'classnames';
import {ObjectiveLine} from 'components/DSYS/icons/strategyIcons';
import {Link} from 'components/DSYS/Link';
import {getDefaultTeamsRoute, ROUTE_PATHS} from 'constant';
import {isSTeam, isUserAdmin} from 'helpers/utils';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router';
import {canAccessAdminPage} from 'views/admin/Admin';
import {isHomeDirectoryOn} from 'views/home/homeDirectory';
import {type ServiceNowNavCategory} from 'views/knowledge/api';
import {useKnowledgeNavMenu} from 'views/knowledge/hooks';

import {KnowledgeNavItem} from './KnowledgeNavItem';
import styles from './LeftNav.module.css';

// DIG adds its elevated overlay offset to this value; keep the tooltip above
// Workstreams' z-index: 12 collapsed toggle without raising the nav itself.
const LEFT_NAV_TOOLTIP_LAYER_Z_INDEX = 20;

// Delay expanding the nav on hover to avoid it sliding open on accidental
// pass-through of the collapsed rail (misclicks / cursor grazing the edge).
const LEFT_NAV_EXPAND_DELAY_MS = 270;

interface NavItem {
  /** Stable id for analytics; `text` is translated. */
  id: string;
  text: string;
  path: string;
  icon: React.ComponentType<React.SVGAttributes<SVGElement>>;
  chipLabel?: string;
  hidden?: boolean;
  state?: {[key: string]: any};
}

export const LeftNav = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const pulseUser = useAtomValue(pulseUserAtom);
  const {employee, employeeTeams} = useAtomValue(loggedInEmployeeAtom);
  const isMobile = useAtomValue(isMobileAtom);
  const growthbook = useAtomValue(growthbookCacheAtom);
  const {isAppBuilderPlatformEnabled, isProductRoadmapEnabled} = growthbook;
  const isHomeDirectory = isHomeDirectoryOn(growthbook);

  const knowledgeEnabled = Boolean(pulseUser) && !isMobile;
  const knowledgeMenuQuery = useKnowledgeNavMenu({
    enabled: knowledgeEnabled,
    cacheScope: employee?.ldap,
  });
  const knowledgeCategories = useMemo<ServiceNowNavCategory[]>(
    () => knowledgeMenuQuery.data?.categories ?? [],
    [knowledgeMenuQuery.data?.categories]
  );
  const showKnowledge = knowledgeEnabled && knowledgeCategories.length > 0;
  const isScorecardsLeftNavEnabledForUser =
    pulseUser?.user_features?.includes('scorecards_left_nav') ?? false;
  const showScorecards =
    isScorecardsLeftNavEnabledForUser || isSTeam(employeeTeams) || isUserAdmin(pulseUser);

  const navRef = useRef<HTMLDivElement>(null);
  const expandTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The knowledge flyout is portaled outside this nav's DOM, so leaving it never
  // fires the nav's `onMouseLeave`. Track the menu's open state to keep the nav
  // expanded while it's open and collapse once it closes.
  const isKnowledgeMenuOpenRef = useRef(false);

  const clearExpandTimeout = () => {
    if (expandTimeoutRef.current) {
      clearTimeout(expandTimeoutRef.current);
      expandTimeoutRef.current = null;
    }
  };

  useEffect(() => clearExpandTimeout, []);

  const handleMouseEnter = () => {
    clearExpandTimeout();
    expandTimeoutRef.current = setTimeout(() => {
      setIsExpanded(true);
    }, LEFT_NAV_EXPAND_DELAY_MS);
  };

  const handleMouseLeave = () => {
    clearExpandTimeout();
    // Keep the nav open while the knowledge flyout is open; the menu's close
    // handler collapses the nav once the menu is dismissed.
    if (isKnowledgeMenuOpenRef.current) {
      return;
    }
    setIsExpanded(false);
  };

  const handleClick = () => {
    clearExpandTimeout();
    setIsExpanded(false);
  };

  const handleKnowledgeMenuToggle = (isOpen: boolean) => {
    isKnowledgeMenuOpenRef.current = isOpen;
    if (isOpen) {
      clearExpandTimeout();
      setIsExpanded(true);
      return;
    }
    // Menu closed: collapse unless the cursor is still over the nav rail (e.g.
    // the user moved from the flyout back onto another nav item).
    if (!navRef.current?.matches(':hover')) {
      setIsExpanded(false);
    }
  };

  const standaloneNavItems = [
    {
      id: 'home',
      text: t('home'),
      path: ROUTE_PATHS.HOME,
      icon: HomeLine,
    },
  ];

  const categorizedNavItems = [
    {
      category: t('Intranet'),
      // When Home is the directory, these items sit directly under it with no divider or label.
      hideHeader: isHomeDirectory,
      items: [
        {
          id: 'people',
          text: t('people'),
          path: ROUTE_PATHS.PEOPLE,
          icon: PersonMultipleLine,
          // Home is the directory and /people redirects there.
          hidden: isHomeDirectory,
        },
        {
          id: 'teams',
          text: t('teams'),
          path: getDefaultTeamsRoute(),
          icon: TeamLine,
        },
        {
          id: 'news',
          text: t('news'),
          path: ROUTE_PATHS.NEWS,
          icon: NewsLine,
        },
        {
          id: 'gratitude',
          text: t('gratitude'),
          path: ROUTE_PATHS.GRATITUDE_BROWSE,
          icon: GratitudeLine,
        },
      ],
      extra: showKnowledge ? (
        <KnowledgeNavItem
          categories={knowledgeCategories}
          chipLabel="Beta"
          onNavigate={handleClick}
          onMenuToggle={handleKnowledgeMenuToggle}
        />
      ) : null,
    },
    {
      category: t('Work'),
      hideHeader: false,
      items: [
        {
          id: 'workstreams',
          text: t('workstreams'),
          path: ROUTE_PATHS.WORKSTREAMS,
          icon: WorkstreamsLine,
        },
        {
          id: 'scorecards',
          text: t('scorecards'),
          path: ROUTE_PATHS.SCORECARDS,
          icon: ObjectiveLine,
          chipLabel: 'Beta',
          hidden: !showScorecards,
        },
        {
          id: 'roadmap',
          text: 'Roadmap',
          path: isProductRoadmapEnabled ? ROUTE_PATHS.ROADMAP_PRODUCT : ROUTE_PATHS.ROADMAP,
          icon: CompassLine,
          chipLabel: 'Beta',
        },
        {
          id: 'apps',
          text: t('apps'),
          path: ROUTE_PATHS.APPS,
          icon: AppsLine,
          chipLabel: 'Beta',
          hidden: !isAppBuilderPlatformEnabled,
        },
      ],
      extra: null,
    },
  ];

  const showAdminButton = canAccessAdminPage(pulseUser);

  return (
    <div
      ref={navRef}
      className={cx(styles.collapsedLeftNav, {
        [styles.expanded]: isExpanded,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.navPaintLayer}>
        <Stack gap="8">
          {standaloneNavItems.map((props, key) => (
            <LinkItem onClick={handleClick} key={`standalone-${key}`} {...props} />
          ))}

          {categorizedNavItems.map((category, categoryIndex) => (
            <Box key={`category-${categoryIndex}`}>
              {!category.hideHeader && (
                <Box marginTop="24" paddingTop="24" className={styles.categoryBorder}>
                  <Box
                    fontFamily="Text"
                    color="Text Subtle"
                    fontWeight="Strong"
                    className={styles.categoryMenu}
                  >
                    {category.category}
                  </Box>
                </Box>
              )}

              <Stack gap="8">
                {category.items
                  .filter((item: NavItem) => !item.hidden)
                  .map((props, key) => (
                    <LinkItem
                      onClick={handleClick}
                      key={`${category.category}-${key}`}
                      {...props}
                    />
                  ))}
                {category.extra}
              </Stack>
            </Box>
          ))}

          {showAdminButton && (
            <Box>
              <Box marginTop="24" paddingTop="24" className={styles.categoryBorder}>
                <Box
                  fontFamily="Text"
                  color="Text Subtle"
                  fontWeight="Strong"
                  className={styles.categoryMenu}
                >
                  {t('admin_mobile')}
                </Box>
              </Box>
              <Stack gap="8">
                <LinkItem
                  onClick={handleClick}
                  id="admin"
                  text={t('admin_controls')}
                  path={ROUTE_PATHS.ADMIN}
                  icon={AdminConsoleLine}
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </div>
    </div>
  );
};

const LinkItem = ({
  onClick,
  id,
  text,
  path,
  icon,
  chipLabel,
  state,
}: {
  id: string;
  text: string;
  icon: React.ComponentType<React.SVGAttributes<SVGElement>>;
  path: string;
  chipLabel?: string;
  onClick?: () => void;
  state?: {[key: string]: any};
}) => {
  const location = useLocation();
  const isMobile = useAtomValue(isMobileAtom);
  const normalizedPath = path.replace(/\/+$/, '');
  const normalizedLocationPath = location.pathname.replace(/\/+$/, '');

  const isActive =
    path === ROUTE_PATHS.HOME
      ? location.pathname === ROUTE_PATHS.HOME
      : normalizedLocationPath === normalizedPath ||
        normalizedLocationPath.startsWith(`${normalizedPath}/`);

  const linkContent = (
    <Link
      to={path ?? location.pathname}
      onClick={() => {
        logNavItemClicked({item: id, path, location: 'left-nav', isCurrent: isActive});
        onClick?.();
      }}
      state={{source: 'left-nav', ...state}}
      hasNoUnderline
      {...withShade({
        duration: 'Instant',
        className: cx(
          styles.itemLink,
          {
            [styles.activeItemLink]: isActive,
          },
          atoms({
            backgroundColor: isActive ? 'Opacity Surface' : 'transparent',
            borderRadius: isMobile ? 'Circular' : 'Medium',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            fontFamily: 'Text',
            fontSize: 'Text Medium',
            paddingY: '4',
            paddingRight: '12',
            paddingLeft: isMobile ? '4' : '8',
          })
        ),
        style: {boxSizing: 'border-box'},
      })}
    >
      {isMobile ? (
        <UIIcon className={atoms({flexShrink: 0})} src={icon} />
      ) : (
        <Box display="flex" alignItems="center">
          <UIIcon className={atoms({flexShrink: 0})} isOpticallyAligned src={icon} />
          <Box marginLeft="10" display="flex" alignItems="center" style={{gap: 6}}>
            <span className={atoms({fontWeight: isActive ? 'Strong' : 'Base'})}>{text}</span>
            {chipLabel ? <span className={styles.navChip}>{chipLabel}</span> : null}
          </Box>
        </Box>
      )}
    </Link>
  );

  const tooltipText = chipLabel ? `${text} ${chipLabel}` : text;

  return (
    <>
      {isMobile ? (
        <LayerContext.Provider value={{zIndex: LEFT_NAV_TOOLTIP_LAYER_Z_INDEX}}>
          <Tooltip title={tooltipText} placement="right">
            <div>{linkContent}</div>
          </Tooltip>
        </LayerContext.Provider>
      ) : (
        linkContent
      )}
    </>
  );
};
